import { useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

import { ApiError } from '@/shared/api-client/workspace';
import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { Textarea } from '@/shared/ui/components/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/components/dialog';
import { Badge } from '@/shared/ui/components/badge';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useAuth } from '@/modules/auth/AuthContext';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { slotTimes, venueSlotGranularityMinutes } from '@/modules/venue/slotGranularity';
import { usePasses } from '@/modules/products/passes';
import { useVenueBookingSettings } from '@/modules/venues/bookingSettings';
import { useVenueOperatingHours } from '@/modules/venues/operatingHours';
import {
  createVenueOperatingWindow,
  type VenueOperatingDayState,
  type VenueOperatingWindow,
} from '@/modules/venues/VenueOperatingWindow';
import {
  useBooking,
  useBookings,
  useCancelBooking,
  useCancelBookingUnit,
  useCreateBooking,
  useEditBooking,
  usePayBooking,
  useRevokeWaiverRecord,
  useRefundBookingUnit,
  type BookingDateFilter,
  type BookingDetail,
  type BookingItem,
  type BookingStatus,
  type BookingUnit,
  type CreateBookingInput,
  type PaymentMethod,
  type WaiverRecord,
  type WaiverStatus,
} from './bookings';

/**
 * The Booking surface in the Venue workspace (PRD #186). It lists Bookings placed on a day by their
 * item SessionSlots (gated by `booking.view`, slice #187, ADR 0048 #263) and — for a user with
 * `booking.create` (#189) — opens a dialog to reserve a booking against one or more PassVariants,
 * each with its own SessionSlot picker for a SESSION pass. The backend 403 is the authoritative
 * gate; the `Forbidden` short-circuit and the conditional New-booking button are UX only.
 */
export function BookingListPage() {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const { venueId } = useVenueContext();

  const scope = { kind: 'venue', venueId } as const;
  const canView = auth.can('booking.view', scope);
  const canCreate = auth.can('booking.create', scope);
  const canEdit = auth.can('booking.edit', scope);
  const canCancel = auth.can('booking.cancel', scope);
  const canPay = auth.can('booking.pay', scope);
  const canRefund = auth.can('booking.refund', scope);
  const canRevokeWaiver = auth.can('venue.manage');

  // The active list view: a Today / Upcoming toggle, or a specific date once one is picked.
  const [tab, setTab] = useState<'today' | 'upcoming'>('today');
  const [date, setDate] = useState('');
  const filter: BookingDateFilter = date
    ? { kind: 'date', date }
    : tab === 'upcoming'
      ? { kind: 'upcoming' }
      : { kind: 'today' };

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const bookings = useBookings(venueId, filter, canView);

  if (!canView) return <Forbidden />;

  const items = bookings.data?.items ?? [];

  return (
    <section className="grid max-w-3xl gap-4 p-6">
      <header className="flex items-start justify-between gap-4">
        <div className="grid gap-1">
          <h1 className="text-2xl font-semibold">{t('bookingList.title')}</h1>
          <p className="text-muted-foreground">{t('bookingList.description')}</p>
        </div>
        {canCreate ? <CreateBookingDialog venueId={venueId} /> : null}
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1" role="group" aria-label={t('bookingList.filters.label')}>
          <Button
            type="button"
            size="sm"
            variant={!date && tab === 'today' ? 'default' : 'outline'}
            aria-pressed={!date && tab === 'today'}
            onClick={() => {
              setDate('');
              setTab('today');
            }}
          >
            {t('bookingList.filters.today')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={!date && tab === 'upcoming' ? 'default' : 'outline'}
            aria-pressed={!date && tab === 'upcoming'}
            onClick={() => {
              setDate('');
              setTab('upcoming');
            }}
          >
            {t('bookingList.filters.upcoming')}
          </Button>
        </div>
        <Input
          type="date"
          aria-label={t('bookingList.filters.onDate')}
          className="w-44"
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />
      </div>

      {bookings.isLoading ? (
        <p className="text-muted-foreground">{t('bookingList.loading')}</p>
      ) : items.length === 0 ? (
        <div className="grid justify-items-center gap-2 rounded-lg border border-dashed p-10 text-center">
          <CalendarClock className="text-muted-foreground size-8" />
          <p className="text-sm font-medium">{t('bookingList.empty.title')}</p>
          <p className="text-muted-foreground text-sm">{t('bookingList.empty.body')}</p>
        </div>
      ) : (
        <ul className="grid gap-2">
          {items.map((booking) => (
            <li key={booking.id}>
              <button
                type="button"
                onClick={() => setSelectedId(booking.id)}
                className="hover:bg-accent flex w-full items-center justify-between rounded-md border p-3 text-left text-sm"
              >
                <span className="grid gap-1">
                  <span className="font-medium">{booking.guestName}</span>
                  <span className="text-muted-foreground font-mono text-xs">
                    {booking.reference}
                  </span>
                </span>
                <span className="text-muted-foreground flex items-center gap-3">
                  <span>{t('bookingList.partySize', { count: booking.partySize })}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <BookingDetailDialog
        venueId={venueId}
        bookingId={selectedId}
        canEdit={canEdit}
        canCancel={canCancel}
        canPay={canPay}
        canRefund={canRefund}
        canRevokeWaiver={canRevokeWaiver}
        onClose={() => setSelectedId(null)}
      />
    </section>
  );
}

/** A SESSION-pass item's SessionSlot (date + `HH:mm:ss` start), rendered in the viewer's locale. */
function formatSlot(dateIso: string, startTime: string): string {
  const [hour, minute] = startTime.split(':');
  const datePart = new Date(`${dateIso}T00:00:00`).toLocaleDateString(undefined, {
    dateStyle: 'medium',
  });
  return `${datePart} · ${hour}:${minute}`;
}

const STATUS_LABEL_KEY: Record<BookingStatus, string> = {
  booked: 'bookingList.status.booked',
  seated: 'bookingList.status.seated',
  cancelled: 'bookingList.status.cancelled',
  paid: 'bookingList.status.paid',
};

const UNIT_STATUS_LABEL_KEY: Record<BookingUnit['status'], string> = {
  available: 'bookingList.units.status.available',
  redeemed: 'bookingList.units.status.redeemed',
  expired: 'bookingList.units.status.expired',
  cancelled: 'bookingList.units.status.cancelled',
  refunded: 'bookingList.units.status.refunded',
};

function unitBadgeVariant(status: BookingUnit['status']): 'secondary' | 'destructive' | 'outline' {
  if (status === 'redeemed') return 'secondary';
  if (status === 'expired' || status === 'cancelled' || status === 'refunded') return 'destructive';
  return 'outline';
}

const WAIVER_STATUS_LABEL_KEY: Record<WaiverStatus, string> = {
  VALID: 'bookingList.units.waiver.currentStatus.VALID',
  EXPIRED: 'bookingList.units.waiver.currentStatus.EXPIRED',
  REVOKED: 'bookingList.units.waiver.currentStatus.REVOKED',
};

function waiverStatusLabel(
  t: ReturnType<typeof useTranslation>['t'],
  status: WaiverStatus | null,
): string {
  return status ? t(WAIVER_STATUS_LABEL_KEY[status]) : t('bookingList.units.waiver.none');
}

function bookingTotal(booking: BookingDetail): number {
  return booking.items.reduce((sum, item) => sum + item.unitPriceAmount * item.quantity, 0);
}

function BookingDetailDialog({
  venueId,
  bookingId,
  canEdit,
  canCancel,
  canPay,
  canRefund,
  canRevokeWaiver,
  onClose,
}: {
  venueId: string;
  bookingId: string | null;
  canEdit: boolean;
  canCancel: boolean;
  canPay: boolean;
  canRefund: boolean;
  canRevokeWaiver: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const detail = useBooking(venueId, bookingId);
  const booking = detail.data;
  const currency = booking?.items[0]?.unitPriceCurrency ?? '';

  const [editing, setEditing] = useState(false);
  const payBooking = usePayBooking(venueId, bookingId ?? '');
  const cancelBooking = useCancelBooking(venueId, bookingId ?? '');
  const cancelUnit = useCancelBookingUnit(venueId, bookingId ?? '');
  const refundUnit = useRefundBookingUnit(venueId, bookingId ?? '');
  const revokeWaiver = useRevokeWaiverRecord(venueId);
  const [revokingUnit, setRevokingUnit] = useState<BookingUnit | null>(null);
  const [revocationReason, setRevocationReason] = useState('');
  const [liveWaiverRecords, setLiveWaiverRecords] = useState<Record<string, WaiverRecord>>({});
  // Edit, cancel, and pay are each offered only for a RESERVED (booked) booking — a SEATED, PAID, or
  // CANCELLED booking is terminal and read-only — and only to a user who holds the matching grant.
  const isReserved = booking?.status === 'booked';
  const showEdit = canEdit && isReserved;
  const showCancel = canCancel && isReserved;
  const showPay = canPay && isReserved;
  const units = booking?.units ?? [];

  function closeRevocationDialog() {
    setRevokingUnit(null);
    setRevocationReason('');
  }

  useEffect(() => {
    setRevokingUnit(null);
    setRevocationReason('');
    setLiveWaiverRecords({});
  }, [bookingId]);

  async function pay(method: PaymentMethod) {
    try {
      await payBooking.mutateAsync(method);
      toast.success(t('bookingList.pay.success'));
      onClose();
    } catch (error) {
      toast.error(bookingErrorMessage(t, error, 'bookingList.errors.payGeneric'));
    }
  }

  async function cancel() {
    try {
      await cancelBooking.mutateAsync();
      toast.success(t('bookingList.cancel.success'));
      onClose();
    } catch (error) {
      toast.error(bookingErrorMessage(t, error, 'bookingList.errors.cancelGeneric'));
    }
  }

  async function cancelOneUnit(unitId: string) {
    try {
      await cancelUnit.mutateAsync(unitId);
      toast.success(t('bookingList.units.cancelSuccess'));
    } catch (error) {
      toast.error(bookingErrorMessage(t, error, 'bookingList.errors.unitCancelGeneric'));
    }
  }

  async function refundOneUnit(unitId: string) {
    try {
      await refundUnit.mutateAsync(unitId);
      toast.success(t('bookingList.units.refundSuccess'));
    } catch (error) {
      toast.error(bookingErrorMessage(t, error, 'bookingList.errors.unitRefundGeneric'));
    }
  }

  async function revokeLinkedWaiver() {
    if (!revokingUnit?.waiverRecordId) return;
    const reason = revocationReason.trim();
    if (!reason) {
      toast.error(t('bookingList.errors.waiverReasonRequired'));
      return;
    }
    try {
      const record = await revokeWaiver.mutateAsync({
        waiverRecordId: revokingUnit.waiverRecordId,
        reason,
      });
      setLiveWaiverRecords((current) => ({ ...current, [record.id]: record }));
      closeRevocationDialog();
      toast.success(t('bookingList.revoke.success'));
    } catch (error) {
      toast.error(bookingErrorMessage(t, error, 'bookingList.errors.waiverRevokeGeneric'));
    }
  }

  const detailDialog = (
    <Dialog
      open={bookingId !== null && !editing}
      onOpenChange={(open) => (open || editing ? null : onClose())}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('bookingList.detail.title')}</DialogTitle>
          <DialogDescription>{t('bookingList.detail.description')}</DialogDescription>
        </DialogHeader>

        {detail.isLoading || !booking ? (
          <p className="text-muted-foreground text-sm">{t('bookingList.detail.loading')}</p>
        ) : (
          <div className="grid gap-4 text-sm">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-muted-foreground">{t('bookingList.detail.customer')}</dt>
              <dd className="font-medium">
                {booking.firstName} {booking.lastName}
              </dd>
              <dt className="text-muted-foreground">{t('bookingList.detail.reference')}</dt>
              <dd className="font-mono">{booking.reference}</dd>
              {booking.phone ? (
                <>
                  <dt className="text-muted-foreground">{t('bookingList.detail.phone')}</dt>
                  <dd>{booking.phone}</dd>
                </>
              ) : null}
              {booking.email ? (
                <>
                  <dt className="text-muted-foreground">{t('bookingList.detail.email')}</dt>
                  <dd>{booking.email}</dd>
                </>
              ) : null}
              <dt className="text-muted-foreground">{t('bookingList.detail.partySize')}</dt>
              <dd>{t('bookingList.partySize', { count: booking.partySize })}</dd>
              <dt className="text-muted-foreground">{t('bookingList.detail.status')}</dt>
              <dd>
                <Badge variant="secondary">{t(STATUS_LABEL_KEY[booking.status])}</Badge>
              </dd>
            </dl>

            <div className="grid gap-2">
              <h3 className="font-medium">{t('bookingList.detail.items')}</h3>
              <ul className="grid gap-1">
                {booking.items.map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-3">
                    <span className="grid">
                      <span>
                        {item.name}
                        <span className="text-muted-foreground"> × {item.quantity}</span>
                      </span>
                      {item.sessionSlotDate && item.sessionSlotStartTime ? (
                        <span className="text-muted-foreground flex items-center gap-1 text-xs">
                          <CalendarClock className="size-3" />
                          {formatSlot(item.sessionSlotDate, item.sessionSlotStartTime)}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-muted-foreground">
                      {item.unitPriceAmount} {item.unitPriceCurrency}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between border-t pt-2 font-medium">
                <span>{t('bookingList.detail.total')}</span>
                <span>
                  {bookingTotal(booking)} {currency}
                </span>
              </div>
            </div>

            {units.length > 0 ? (
              <div className="grid gap-2">
                <h3 className="font-medium">{t('bookingList.units.title')}</h3>
                <ul className="grid gap-1">
                  {units.map((unit) => (
                    <li key={unit.id} className="grid gap-2 rounded-md border p-3">
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2">
                          <Badge variant={unitBadgeVariant(unit.status)}>
                            {t(UNIT_STATUS_LABEL_KEY[unit.status])}
                          </Badge>
                          <span className="text-muted-foreground text-xs">#{unit.unitIndex}</span>
                        </span>
                        <div className="flex items-center gap-2">
                          {canCancel && unit.cancellable ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => cancelOneUnit(unit.id)}
                              disabled={cancelUnit.isPending}
                            >
                              {t('bookingList.units.cancelAction')}
                            </Button>
                          ) : null}
                          {canRefund && unit.refundable ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => refundOneUnit(unit.id)}
                              disabled={refundUnit.isPending}
                            >
                              {t('bookingList.units.refundAction')}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                      <WaiverSummary
                        unit={unit}
                        t={t}
                        record={
                          unit.waiverRecordId ? liveWaiverRecords[unit.waiverRecordId] : undefined
                        }
                        canRevokeWaiver={canRevokeWaiver}
                        revokePending={revokeWaiver.isPending}
                        onRevoke={() => setRevokingUnit(unit)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {showEdit || showCancel || showPay ? (
              <DialogFooter className="flex-wrap gap-2 sm:justify-start">
                {showEdit ? (
                  <Button type="button" variant="outline" onClick={() => setEditing(true)}>
                    {t('bookingList.edit.action')}
                  </Button>
                ) : null}
                {showCancel ? (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={cancel}
                    disabled={cancelBooking.isPending}
                  >
                    {t('bookingList.cancel.action')}
                  </Button>
                ) : null}
                {showPay ? (
                  <>
                    <Button
                      type="button"
                      onClick={() => pay('cash')}
                      disabled={payBooking.isPending}
                    >
                      {t('bookingList.pay.cash')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => pay('card_manual')}
                      disabled={payBooking.isPending}
                    >
                      {t('bookingList.pay.card')}
                    </Button>
                  </>
                ) : null}
              </DialogFooter>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      {detailDialog}
      <Dialog
        open={revokingUnit !== null}
        onOpenChange={(open) => (open ? null : closeRevocationDialog())}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('bookingList.revoke.title')}</DialogTitle>
            <DialogDescription>{t('bookingList.revoke.description')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="waiver-revocation-reason">{t('bookingList.revoke.reason')}</Label>
            <Textarea
              id="waiver-revocation-reason"
              value={revocationReason}
              onChange={(event) => setRevocationReason(event.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeRevocationDialog}>
              {t('bookingList.create.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void revokeLinkedWaiver()}
              disabled={revokeWaiver.isPending}
            >
              {revokeWaiver.isPending
                ? t('bookingList.revoke.submitting')
                : t('bookingList.revoke.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {booking ? (
        <EditBookingDialog
          venueId={venueId}
          booking={booking}
          open={editing}
          onOpenChange={setEditing}
          onSaved={() => {
            setEditing(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}

function WaiverSummary({
  unit,
  t,
  record,
  canRevokeWaiver,
  revokePending,
  onRevoke,
}: {
  unit: BookingUnit;
  t: ReturnType<typeof useTranslation>['t'];
  record?: WaiverRecord;
  canRevokeWaiver: boolean;
  revokePending: boolean;
  onRevoke: () => void;
}) {
  const currentStatus = record?.status ?? unit.waiverStatusAtLink;
  const canRevoke = canRevokeWaiver && !!unit.waiverRecordId && currentStatus !== 'REVOKED';
  const signerName = unit.waiverSignerName ?? t('bookingList.units.waiver.none');

  if (!unit.requiresWaiver && !unit.waiverRecordId) return null;

  return (
    <div className="bg-muted/30 grid gap-1 rounded-md p-2 text-xs">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium">{t('bookingList.units.waiver.title')}</span>
        {canRevoke ? (
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={onRevoke}
            disabled={revokePending}
          >
            {t('bookingList.units.waiver.revoke')}
          </Button>
        ) : null}
      </div>
      {unit.waiverRecordId ? (
        <>
          <p>{`${t('bookingList.units.waiver.signer')}: ${signerName}`}</p>
          <p>
            {`${t('bookingList.units.waiver.statusAtLink')}: ${waiverStatusLabel(t, unit.waiverStatusAtLink)}`}
          </p>
          <p>
            {`${t('bookingList.units.waiver.currentStatusLabel')}: ${waiverStatusLabel(t, currentStatus)}`}
          </p>
          {record?.revokedReason ? (
            <p>
              {t('bookingList.revoke.reason')}: {record.revokedReason}
            </p>
          ) : null}
        </>
      ) : (
        <p>{t('bookingList.units.waiver.required')}</p>
      )}
    </div>
  );
}

/**
 * The toast message for a failed Booking action. A coded {@link ApiError} maps to its specific
 * `bookingList.errors.<code>` copy when one exists; everything else falls back to the per-action
 * generic key the caller names.
 */
function bookingErrorMessage(
  t: ReturnType<typeof useTranslation>['t'],
  error: unknown,
  fallbackKey: string,
): string {
  if (error instanceof ApiError && error.code) {
    const key = `bookingList.errors.${error.code}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }
  return t(fallbackKey);
}

/** A form line's SessionSlot draft: a plain date + one of {@link SLOT_TIMES}, only for a SESSION variant. */
interface SlotDraft {
  date: string;
  time: string;
}

/** The edit/create form's fields, all kept as strings until submit projects them onto the API shape. */
interface BookingFormValues {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  partySize: string;
  quantities: Record<string, string>;
  slots: Record<string, SlotDraft>;
}

const EMPTY_BOOKING_FORM: BookingFormValues = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  partySize: '1',
  quantities: {},
  slots: {},
};

type PassVariantOption = {
  id: string;
  label: string;
  price: string;
  /** Whether this variant is a SESSION pass and so needs a SessionSlot (ADR 0048, #263). */
  isSession: boolean;
  sessionDurationMinutes: number | null;
};

/**
 * The bookable PassVariant options at a Venue: the active variants of its active passes. Loaded
 * only while `enabled` (i.e. a form is open) and only for a user who may read passes.
 */
function usePassVariantOptions(venueId: string, enabled: boolean) {
  const passes = usePasses(venueId, undefined, enabled);
  const variants = useMemo<PassVariantOption[]>(
    () =>
      (passes.data?.items ?? [])
        .filter((pass) => pass.active)
        .flatMap((pass) =>
          pass.variations
            .filter((variation) => variation.active)
            .map((variation) => ({
              id: variation.id,
              label: `${pass.name} · ${variation.name}`,
              price: `${variation.amount} ${variation.currency}`,
              isSession: pass.type === 'SESSION',
              sessionDurationMinutes: pass.type === 'SESSION' ? pass.sessionDurationMinutes : null,
            })),
        ),
    [passes.data],
  );
  return { variants, isLoading: passes.isLoading };
}

function slotTimesForVariant(
  slotGranularityMinutes: number | undefined,
  operatingWindow: VenueOperatingWindow | undefined,
  date: string,
  sessionDurationMinutes: number | null,
): string[] {
  if (!date || sessionDurationMinutes == null || operatingWindow == null) {
    return slotTimes(slotGranularityMinutes);
  }
  return [...operatingWindow.offerableStartTimes(date, sessionDurationMinutes)];
}

function slotAvailabilityMessage(
  t: TFunction<'common'>,
  dayState: VenueOperatingDayState | null,
): string | null {
  switch (dayState) {
    case 'closed':
      return t('bookingList.create.slotDateClosed');
    case 'unconfigured':
      return t('bookingList.create.slotHoursUnconfigured');
    default:
      return null;
  }
}

/** Project the string-valued form onto the API's create/edit body, dropping zero-quantity lines. */
function buildBookingInput(
  values: BookingFormValues,
  variants: PassVariantOption[],
): CreateBookingInput {
  const lines = variants
    .map((variant) => {
      const quantity = Number(values.quantities[variant.id] ?? 0);
      if (quantity <= 0) return null;
      if (!variant.isSession) {
        return { passVariantId: variant.id, quantity };
      }
      const slot = values.slots[variant.id];
      return {
        passVariantId: variant.id,
        quantity,
        sessionSlotDate: slot?.date,
        sessionSlotStartTime: slot?.time ? `${slot.time}:00` : undefined,
      };
    })
    .filter((line): line is NonNullable<typeof line> => line !== null);

  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    phone: values.phone.trim() ? values.phone.trim() : undefined,
    email: values.email.trim() ? values.email.trim() : undefined,
    partySize: Number(values.partySize),
    items: lines,
  };
}

/** A saved booking projected back into the edit form's string fields, seeding quantities and slots. */
function bookingToFormValues(booking: BookingDetail): BookingFormValues {
  const quantities: Record<string, string> = {};
  const slots: Record<string, SlotDraft> = {};
  for (const item of booking.items) {
    quantities[item.passVariantId] = String(item.quantity);
    if (item.sessionSlotDate && item.sessionSlotStartTime) {
      slots[item.passVariantId] = {
        date: item.sessionSlotDate,
        time: item.sessionSlotStartTime.slice(0, 5),
      };
    }
  }
  return {
    firstName: booking.firstName,
    lastName: booking.lastName,
    phone: booking.phone ?? '',
    email: booking.email ?? '',
    partySize: String(booking.partySize),
    quantities,
    slots,
  };
}

/** The shared booking form fields, driven by a single value object and a patch callback. */
function BookingFormBody({
  venueId,
  values,
  onChange,
  variants,
  passesLoading,
}: {
  venueId: string;
  values: BookingFormValues;
  onChange: (patch: Partial<BookingFormValues>) => void;
  variants: PassVariantOption[];
  passesLoading: boolean;
}) {
  const { t } = useTranslation('common');
  const { venues } = useAuth();
  const bookingSettings = useVenueBookingSettings(venueId);
  const operatingHours = useVenueOperatingHours(venueId);
  const venue = venues.find((entry) => entry.id === venueId);
  const operatingWindow = useMemo(() => {
    if (venue == null || operatingHours.data == null) return undefined;
    return createVenueOperatingWindow({
      timezone: venue.timezone,
      slotGranularityMinutes: venueSlotGranularityMinutes(venue.slotGranularityMinutes),
      operatingHoursConfigured: operatingHours.data.configured !== false,
      weekdayHours: operatingHours.data.weekdayHours,
    });
  }, [operatingHours.data, venue]);
  const today = new Date().toISOString().slice(0, 10);
  const maxDate = bookingSettings.data
    ? new Date(Date.now() + bookingSettings.data.bookingHorizonDays * 86_400_000)
        .toISOString()
        .slice(0, 10)
    : undefined;

  function setSlot(variantId: string, patch: Partial<SlotDraft>) {
    const current = values.slots[variantId] ?? { date: '', time: '' };
    onChange({ slots: { ...values.slots, [variantId]: { ...current, ...patch } } });
  }

  useEffect(() => {
    const nextSlots = { ...values.slots };
    let changed = false;

    for (const variant of variants) {
      if (!variant.isSession) continue;
      const slot = values.slots[variant.id];
      if (!slot?.time) continue;
      const nextTimeOptions = slotTimesForVariant(
        venue?.slotGranularityMinutes,
        operatingWindow,
        slot.date,
        variant.sessionDurationMinutes,
      );
      if (nextTimeOptions.includes(slot.time)) continue;
      nextSlots[variant.id] = { ...slot, time: '' };
      changed = true;
    }

    if (changed) {
      onChange({ slots: nextSlots });
    }
  }, [onChange, operatingWindow, values.slots, variants, venue?.slotGranularityMinutes]);

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="booking-first-name">{t('bookingList.create.firstName')}</Label>
          <Input
            id="booking-first-name"
            value={values.firstName}
            onChange={(event) => onChange({ firstName: event.target.value })}
            required
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="booking-last-name">{t('bookingList.create.lastName')}</Label>
          <Input
            id="booking-last-name"
            value={values.lastName}
            onChange={(event) => onChange({ lastName: event.target.value })}
            required
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="booking-phone">{t('bookingList.create.phone')}</Label>
          <Input
            id="booking-phone"
            value={values.phone}
            onChange={(event) => onChange({ phone: event.target.value })}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="booking-email">{t('bookingList.create.email')}</Label>
          <Input
            id="booking-email"
            type="email"
            value={values.email}
            onChange={(event) => onChange({ email: event.target.value })}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="booking-party-size">{t('bookingList.create.partySize')}</Label>
          <Input
            id="booking-party-size"
            type="number"
            min={1}
            value={values.partySize}
            onChange={(event) => onChange({ partySize: event.target.value })}
            required
          />
        </div>
      </div>

      <fieldset className="grid gap-2">
        <legend className="text-sm font-medium">{t('bookingList.create.items')}</legend>
        {passesLoading ? (
          <p className="text-muted-foreground text-sm">{t('bookingList.create.loadingItems')}</p>
        ) : variants.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('bookingList.create.noItems')}</p>
        ) : (
          <ul className="grid gap-2">
            {variants.map((variant) => {
              const quantity = Number(values.quantities[variant.id] ?? 0);
              const slot = values.slots[variant.id];
              const slotAvailabilityMessageText = slotAvailabilityMessage(
                t,
                slot?.date && operatingWindow ? operatingWindow.dayState(slot.date) : null,
              );
              const timeOptions = slotTimesForVariant(
                venue?.slotGranularityMinutes,
                operatingWindow,
                slot?.date ?? '',
                variant.sessionDurationMinutes,
              );
              return (
                <li key={variant.id} className="grid gap-2 rounded-md border p-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm">
                      <span className="font-medium">{variant.label}</span>
                      <span className="text-muted-foreground"> — {variant.price}</span>
                    </span>
                    <Input
                      type="number"
                      min={0}
                      aria-label={`${t('bookingList.create.quantity')} — ${variant.label}`}
                      className="w-20"
                      value={values.quantities[variant.id] ?? ''}
                      onChange={(event) =>
                        onChange({
                          quantities: { ...values.quantities, [variant.id]: event.target.value },
                        })
                      }
                    />
                  </div>
                  {variant.isSession && quantity > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="grid gap-1">
                        <Label htmlFor={`booking-slot-date-${variant.id}`}>
                          {t('bookingList.create.slotDate')}
                        </Label>
                        <Input
                          id={`booking-slot-date-${variant.id}`}
                          type="date"
                          min={today}
                          max={maxDate}
                          value={slot?.date ?? ''}
                          onChange={(event) => setSlot(variant.id, { date: event.target.value })}
                          required
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor={`booking-slot-time-${variant.id}`}>
                          {t('bookingList.create.slotTime')}
                        </Label>
                        <select
                          id={`booking-slot-time-${variant.id}`}
                          className="border-input h-9 rounded-md border bg-transparent px-3 text-sm"
                          value={slot?.time ?? ''}
                          onChange={(event) => setSlot(variant.id, { time: event.target.value })}
                          required
                        >
                          <option value="" disabled>
                            {t('bookingList.create.slotTimePlaceholder')}
                          </option>
                          {timeOptions.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>
                        {slotAvailabilityMessageText ? (
                          <p className="text-muted-foreground text-xs">
                            {slotAvailabilityMessageText}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </fieldset>
    </>
  );
}

/**
 * The shared booking form dialog used to create (#189) and edit (#191) a booking — both take the same
 * customer/party/PassVariant body, each SESSION line carrying its own SessionSlot. It re-seeds from
 * `initial` each time it opens, so a reopened create form is blank and an edit form shows the saved
 * booking; the submit body is identical, only the caller's `onSubmit` (POST vs PUT) and labels differ.
 */
function BookingFormDialog({
  venueId,
  open,
  onOpenChange,
  trigger,
  title,
  description,
  submitLabel,
  pending,
  initial,
  onSubmit,
}: {
  venueId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  trigger?: React.ReactNode;
  title: string;
  description: string;
  submitLabel: string;
  pending: boolean;
  initial: BookingFormValues;
  onSubmit: (input: CreateBookingInput) => Promise<void>;
}) {
  const { t } = useTranslation('common');
  const { variants, isLoading } = usePassVariantOptions(venueId, open);
  const [values, setValues] = useState<BookingFormValues>(initial);

  // Re-seed whenever the dialog opens. `initial` is stable per open (EMPTY for create, memoized per
  // booking for edit), so this does not clobber the user's edits while they type.
  useEffect(() => {
    if (open) setValues(initial);
  }, [open, initial]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    await onSubmit(buildBookingInput(values, variants));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <form className="grid gap-4" onSubmit={submit}>
          <BookingFormBody
            venueId={venueId}
            values={values}
            onChange={(patch) => setValues((current) => ({ ...current, ...patch }))}
            variants={variants}
            passesLoading={isLoading}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t('bookingList.create.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateBookingDialog({ venueId }: { venueId: string }) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const createBooking = useCreateBooking(venueId);

  async function submit(input: CreateBookingInput) {
    try {
      await createBooking.mutateAsync(input);
      toast.success(t('bookingList.create.success'));
      setOpen(false);
    } catch (error) {
      toast.error(bookingErrorMessage(t, error, 'bookingList.errors.generic'));
    }
  }

  return (
    <BookingFormDialog
      venueId={venueId}
      open={open}
      onOpenChange={setOpen}
      trigger={<Button>{t('bookingList.newBooking')}</Button>}
      title={t('bookingList.create.title')}
      description={t('bookingList.create.itemsHint')}
      submitLabel={t('bookingList.create.submit')}
      pending={createBooking.isPending}
      initial={EMPTY_BOOKING_FORM}
      onSubmit={submit}
    />
  );
}

/**
 * Edits a RESERVED booking (#191). Opened from the detail dialog; pre-fills from the saved booking and
 * PUTs the same body shape as create. Unchanged items keep their frozen snapshot server-side; the
 * backend 409s a terminal booking, but the detail dialog only offers this for a booked one.
 */
function EditBookingDialog({
  venueId,
  booking,
  open,
  onOpenChange,
  onSaved,
}: {
  venueId: string;
  booking: BookingDetail;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('common');
  const editBooking = useEditBooking(venueId, booking.id);
  const initial = useMemo(() => bookingToFormValues(booking), [booking]);

  async function submit(input: CreateBookingInput) {
    try {
      await editBooking.mutateAsync(input);
      toast.success(t('bookingList.edit.success'));
      onSaved();
    } catch (error) {
      toast.error(bookingErrorMessage(t, error, 'bookingList.errors.editGeneric'));
    }
  }

  return (
    <BookingFormDialog
      venueId={venueId}
      open={open}
      onOpenChange={onOpenChange}
      title={t('bookingList.edit.title')}
      description={t('bookingList.edit.itemsHint')}
      submitLabel={t('bookingList.edit.submit')}
      pending={editBooking.isPending}
      initial={initial}
      onSubmit={submit}
    />
  );
}

// Re-exported only so callers importing `BookingItem` from this module for tests keep compiling.
export type { BookingItem };
